import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customPagesService } from "@/services/customPagesService";
import { CreateCustomPageDto, UpdateCustomPageDto } from "@/types/custom-page";

export const customPageKeys = {
  all: ["custom-pages"] as const,
  lists: () => [...customPageKeys.all, "list"] as const,
  list: () => [...customPageKeys.lists()] as const,
  details: () => [...customPageKeys.all, "detail"] as const,
  detail: (id: string) => [...customPageKeys.details(), id] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

const invalidateCustomPageRelatedQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: customPageKeys.all });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
};

export const useCustomPages = () =>
  useQuery({
    queryKey: customPageKeys.list(),
    queryFn: () => customPagesService.getCustomPages(),
  });

export const useCustomPage = (id: string) =>
  useQuery({
    queryKey: customPageKeys.detail(id),
    queryFn: () => customPagesService.getCustomPage(id),
    enabled: Boolean(id),
  });

export const useCreateCustomPage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomPageDto) =>
      customPagesService.createCustomPage(data),
    onSuccess: () => {
      invalidateCustomPageRelatedQueries(queryClient);
    },
  });
};

export const useUpdateCustomPage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomPageDto }) =>
      customPagesService.updateCustomPage(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: customPageKeys.detail(variables.id),
      });
      invalidateCustomPageRelatedQueries(queryClient);
    },
  });
};

export const useDeleteCustomPage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customPagesService.deleteCustomPage(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: customPageKeys.detail(deletedId) });
      invalidateCustomPageRelatedQueries(queryClient);
    },
  });
};
